import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart3 } from "lucide-react";

const money = (n) => (n < 0 ? "-$" : "$") + Math.abs(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 });

function Scorecards() {
  const [data, setData] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => { api.get(`/management/scorecards?month=${month}&year=${month.slice(0, 4)}`).then(({ data }) => setData(data.items)).catch(() => toast.error("Load failed")); }, [month]);
  const cols = [["producer_name", "Producer"], ["written_premium_ytd", "Premium YTD", money], ["agency_commission_ytd", "Agency Comm", money], ["producer_commission_ytd", "Prod Comm YTD", money], ["new_business_ytd", "New Biz"], ["renewals_ytd", "Renewals"], ["close_rate", "Close %", (v) => `${v}%`], ["pipeline_value", "Pipeline", money], ["apollo_prospects_added", "Apollo"]];
  return (
    <div>
      <Input type="month" data-testid="scorecard-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 mb-3" />
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{cols.map(([, l]) => <th key={l} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{l}</th>)}</tr></thead>
          <tbody>{data.map((r) => (
            <tr key={r.producer_user_id} data-testid={`scorecard-${r.producer_user_id}`} className="border-b border-border last:border-0 hover:bg-ice">
              {cols.map(([k, , fmt]) => <td key={k} className="px-3 py-2 whitespace-nowrap">{k === "producer_name" ? <span className="text-slate font-medium">{r[k]}</span> : (fmt ? fmt(r[k]) : r[k])}</td>)}
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/management/admin-dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-mutedsea">Loading…</p>;
  const cards = [["Total Written Premium", d.total_written_premium], ["Agency Commission", d.agency_commission], ["Producer Commission", d.producer_commission], ["Net Agency Retained", d.agency_retained]];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="admin-dashboard">
        {cards.map(([l, v]) => <div key={l} className="bg-white border border-border rounded-sm p-4"><div className="text-xs uppercase text-mutedsea">{l}</div><div className="font-serif text-2xl text-navy mt-1">{money(v)}</div></div>)}
      </div>
      <h3 className="font-serif text-lg text-navy mb-2">Production by Producer</h3>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{["Producer", "Premium YTD", "Producer Comm", "Close %"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>{(d.by_producer || []).map((p) => <tr key={p.producer_user_id} className="border-b border-border last:border-0"><td className="px-3 py-2 text-slate">{p.producer_name}</td><td className="px-3 py-2">{money(p.written_premium_ytd)}</td><td className="px-3 py-2">{money(p.producer_commission_ytd)}</td><td className="px-3 py-2">{p.close_rate}%</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function Aging() {
  const [d, setD] = useState(null);
  const load = () => api.get("/management/reconciliation-aging").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const enterActual = async (id) => {
    const v = window.prompt("Actual agency commission received ($):"); if (!v) return;
    try { const { data } = await api.post(`/commissions/ledger/${id}/actual`, { actual_agency_commission: parseFloat(v) }); toast.success(`Reconciled (adj ${data.adjustment}).`); load(); }
    catch (e) { toast.error("Reconcile failed"); }
  };
  if (!d) return <p className="text-mutedsea">Loading…</p>;
  return (
    <div data-testid="aging-report">
      <div className="grid grid-cols-4 gap-3 mb-4">
        {Object.entries(d.summary).map(([k, v]) => <div key={k} className={`border rounded-sm p-3 ${k === "90+" || k === "61-90" ? "bg-seared/5 border-seared/30" : "bg-white border-border"}`}><div className="text-xs uppercase text-mutedsea">{k} days</div><div className="font-serif text-xl text-navy">{v.count}</div><div className="text-xs text-mutedsea">{money(v.estimated)} est.</div></div>)}
      </div>
      {d.over_60_count > 0 && <div className="bg-seared/10 border border-seared/30 rounded-sm p-3 mb-4 text-sm text-seared" data-testid="aging-alert">⚠ {d.over_60_count} estimated commission(s) outstanding over 60 days — enter carrier actuals to reconcile.</div>}
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{["Producer", "Carrier", "Effective", "Est. Agency", "Est. Producer", "Days", ""].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>{["90+", "61-90", "31-60", "0-30"].flatMap((b) => (d.buckets[b] || []).map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0 hover:bg-ice">
              <td className="px-3 py-2 text-slate">{r.producer_name}</td><td className="px-3 py-2 text-xs">{r.carrier}</td>
              <td className="px-3 py-2 text-xs">{r.effective_date}</td><td className="px-3 py-2">{money(r.expected_agency_commission)}</td>
              <td className="px-3 py-2">{money(r.estimated_producer_commission)}</td>
              <td className="px-3 py-2"><Badge variant="outline" className={r.days_outstanding > 60 ? "text-seared border-seared/40" : ""}>{r.days_outstanding}d</Badge></td>
              <td className="px-3 py-2"><button data-testid={`reconcile-${r.id}`} onClick={() => enterActual(r.id)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Enter Actual</button></td>
            </tr>)))}</tbody>
        </table>
      </div>
    </div>
  );
}

function ApolloLimits() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/management/apollo/limits").then(({ data }) => setUsers(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (u, patch) => {
    try { await api.put(`/management/apollo/limits/${u.id}`, { ...u.intel_limits, ...patch }); toast.success("Saved."); load(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid="apollo-limits-table">
      <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{["User", "Enabled", "Daily Reveals", "Monthly Credits", "Used Today", "Month Credits"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.id} data-testid={`limit-${u.id}`} className="border-b border-border last:border-0">
            <td className="px-3 py-2"><div className="text-slate">{u.name}</div><div className="text-xs text-mutedsea">{u.role}</div></td>
            <td className="px-3 py-2"><input type="checkbox" data-testid={`limit-enabled-${u.id}`} checked={u.intel_limits.enabled} onChange={(e) => save(u, { enabled: e.target.checked })} /></td>
            <td className="px-3 py-2"><Input type="number" data-testid={`limit-reveals-${u.id}`} defaultValue={u.intel_limits.daily_reveals} onBlur={(e) => save(u, { daily_reveals: parseInt(e.target.value || 0) })} className="w-20 h-8" /></td>
            <td className="px-3 py-2"><Input type="number" data-testid={`limit-credits-${u.id}`} defaultValue={u.intel_limits.monthly_credits} onBlur={(e) => save(u, { monthly_credits: parseInt(e.target.value || 0) })} className="w-24 h-8" /></td>
            <td className="px-3 py-2 text-xs text-mutedsea">{u.usage_today.reveals}r / {u.usage_today.credits}c</td>
            <td className="px-3 py-2 text-xs text-mutedsea">{u.usage_month_credits}</td>
          </tr>))}</tbody>
      </table>
      <p className="text-xs text-mutedsea p-3">0 = unlimited (agency-wide caps still apply). Limits are enforced server-side; exceeding them returns a clear error and consumes no credits.</p>
    </div>
  );
}

export default function Management() {
  return (
    <div className="p-6" data-testid="management-page">
      <div className="flex items-center gap-2 mb-1"><BarChart3 className="h-6 w-6 text-navy" /><h1 className="font-serif text-2xl text-navy">Management</h1></div>
      <p className="text-sm text-mutedsea mb-5">Producer performance, agency production, reconciliation aging and intelligence usage controls.</p>
      <Tabs defaultValue="scorecards">
        <TabsList className="bg-cloud">
          {[["scorecards", "Scorecards"], ["dashboard", "Agency Dashboard"], ["aging", "Reconciliation Aging"], ["apollo", "Apollo Limits"]].map(([v, l]) =>
            <TabsTrigger key={v} value={v} data-testid={`mgmt-tab-${v}`} className="data-[state=active]:bg-navy data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="scorecards" className="mt-4"><Scorecards /></TabsContent>
        <TabsContent value="dashboard" className="mt-4"><AdminDashboard /></TabsContent>
        <TabsContent value="aging" className="mt-4"><Aging /></TabsContent>
        <TabsContent value="apollo" className="mt-4" data-testid="apollo-limits"><ApolloLimits /></TabsContent>
      </Tabs>
    </div>
  );
}
